/**
 * Security settings page — /security
 *
 * Authenticated users can:
 * - Enable/disable TOTP 2FA (scan QR, enter code, view backup codes)
 * - Register, list, and delete passkeys (WebAuthn)
 * - Regenerate backup codes
 * - Set a password (OAuth-only users) to unlock 2FA
 * - Re-setup 2FA after recovery code login
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@better-bookkeeping/ui/button";
import { Input } from "@better-bookkeeping/ui/input";
import { MaskedInput } from "@better-bookkeeping/ui/masked-input";
import { Label } from "@better-bookkeeping/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@better-bookkeeping/ui/alert";
import { Badge } from "@better-bookkeeping/ui/badge";
import { Checkbox } from "@better-bookkeeping/ui/checkbox";
import { Separator } from "@better-bookkeeping/ui/separator";
import {
  AlertCircle,
  Fingerprint,
  Plus,
  Trash2,
  Loader2,
  ShieldCheck,
  Copy,
  Download,
  LogOut,
  AlertTriangle,
  RefreshCw,
  Smartphone,
  Mail,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { z } from "zod";
import { QRCodeSVG } from "qrcode.react";
import { authClient, orpcClient } from "@/lib/auth-client";
import { useAuth } from "@/hooks/useAuth";
import { useRuntimeConfig } from "@/lib/runtime-config";
import { safePostAuthRedirect } from "@/lib/redirect-utils";
import { navigateToFinalDestination } from "@/lib/post-auth-redirect";
import { useDevOverride } from "@/lib/dev/dev-overrides";
import { classifyPasskeyError } from "@/lib/passkey-errors";
import { buildEnrollMfaCallbackURL } from "@/lib/auth-flow-redirects";
import { preparePendingInvitationAuth, withInviteTokenSearch } from "@/lib/pending-pricing-invite";
import { OtpDigits } from "@/components/otp-digits";
import { SignedInPanel } from "@/components/signed-in-panel";
import { PasswordInput } from "@better-bookkeeping/ui/password-input";

/**
 * Phone number helpers (AUTH-123) — US-only for now.
 * `displayToE164("(555) 123-4567")` → `"+15551234567"`
 * `e164ToDisplay("+15551234567")` → `"(555) 123-4567"` (any non-US E.164 falls
 * back to the raw input so we never lose a previously stored international
 * number; the masked input will reformat as the user edits).
 */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

function displayToE164(masked: string): string {
  const digits = digitsOnly(masked);
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return "";
}

function e164ToDisplay(e164: string): string {
  if (!e164) return "";
  const digits = digitsOnly(e164);
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return e164; // non-US — let user re-enter
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

function maskPhoneLastFour(phoneNumber: string): string {
  const digits = digitsOnly(phoneNumber);
  const lastFour = digits.slice(-4);
  return lastFour ? `***-***-${lastFour}` : "your phone";
}

export const Route = createFileRoute("/security")({
  validateSearch: withInviteTokenSearch({
    setup: z.boolean().optional(),
    recovery: z.boolean().optional(),
    redirect_uri: z.string().optional(),
  }).parse,
  component: SecurityRoutePage,
});

function SecurityRoutePage() {
  const { setup, recovery, redirect_uri } = Route.useSearch();
  return <SecurityPage setup={setup} recovery={recovery} redirect_uri={redirect_uri} />;
}

interface Passkey {
  id: string;
  name: string | null;
  credentialID: string;
  deviceType: string;
  createdAt: string;
}

type TwoFactorStep =
  | "idle"
  | "enabling"
  | "verify"
  | "backup-codes"
  | "disabling"
  | "regenerating-codes"
  | "regenerated-codes";

type OtpDeliveryMethod = "SMS" | "EMAIL";

interface MigrationConfirmation {
  method: OtpDeliveryMethod;
  target: string;
}

export interface SecurityPageProps {
  setup?: boolean;
  recovery?: boolean;
  redirect_uri?: string;
}

export function SecurityPage({ setup, recovery, redirect_uri }: SecurityPageProps) {
  const { user: realUser, isLoading: realAuthLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const runtimeConfig = useRuntimeConfig();

  // AUTH-122 (DEV-only): when the toolbar's "Bypass Auth" toggle is on,
  // skip the real-user requirement and substitute a stub so every panel
  // can be previewed unauthenticated. The stub satisfies the few fields
  // the page actually reads (email + twoFactorEnabled).
  const bypassAuthGate = useDevOverride((s) => s.bypassAuthGate);
  const devBypassActive = import.meta.env.DEV && bypassAuthGate;
  const stubUser = devBypassActive
    ? ({
        id: "dev-stub-user",
        email: "dev@example.com",
        name: "Dev Stub User",
        image: null,
        twoFactorEnabled: false,
      } as unknown as typeof realUser)
    : null;
  const user = realUser ?? stubUser;
  const authLoading = devBypassActive ? false : realAuthLoading;

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState<TwoFactorStep>("idle");
  const [totpURI, setTotpURI] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verifyCode, setVerifyCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [regenPassword, setRegenPassword] = useState("");

  // Passkey state
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [passkeysLoading, setPasskeysLoading] = useState(true);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [passkeyName, setPasskeyName] = useState("");

  // Phone / SMS-2FA state (AUTH-123)
  // `phoneInput` is the user-facing masked value, e.g. "(555) 123-4567".
  // E.164 (`+15551234567`) is derived at submit time. US numbers only for now.
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneStep, setPhoneStep] = useState<"idle" | "code-sent" | "backup-codes">("idle");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);

  // AUTH-123: top-level mode selector. When non-null, the page is in the
  // "configuring this method" sub-flow. When null, the page renders either
  // the picker (no methods yet) or the manage list (≥1 method enrolled).
  type MethodKey = "totp" | "phone" | "passkey" | "email";
  const [methodInFlow, setMethodInFlow] = useState<MethodKey | null>(null);

  // AUTH-128: email-OTP support. Track verified-email status (gates the
  // Email picker option) and the user's saved delivery preference.
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailOtpStep, setEmailOtpStep] = useState<"idle" | "code-sent" | "backup-codes">("idle");
  const [emailOtpCode, setEmailOtpCode] = useState("");
  const [otpDeliveryMethod, setOtpDeliveryMethodState] = useState<"SMS" | "EMAIL" | null>(null);
  const [otpMethodLoading, setOtpMethodLoading] = useState(false);
  const [migrationConfirmation, setMigrationConfirmation] = useState<MigrationConfirmation | null>(
    null,
  );
  const [migrationStep, setMigrationStep] = useState<"idle" | "code-sent" | "backup-codes">("idle");
  const [migrationCode, setMigrationCode] = useState("");
  const [migrationLoading, setMigrationLoading] = useState(false);

  // AUTH-124: step-up state. When set, a modal prompts the user for password
  // (or passkey) to elevate their session. After success, the original action
  // (`pendingAction`) is retried automatically.
  const [stepUpPending, setStepUpPending] = useState<null | {
    reason: string;
    retry: () => Promise<void> | void;
  }>(null);
  const [stepUpPassword, setStepUpPassword] = useState("");
  const [stepUpLoading, setStepUpLoading] = useState(false);
  const [stepUpHasPassword, setStepUpHasPassword] = useState<boolean | null>(null);

  // Shared state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [securityStateLoading, setSecurityStateLoading] = useState(true);

  // AUTH-122 (DEV-only): pin twoFactorStep from the DevToolbar so every panel
  // can be previewed without going through real flows. Also seeds dummy
  // backup codes for the panels that render them, otherwise they'd render
  // empty.
  const devStepOverride = useDevOverride((s) => s.securityStep);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!devStepOverride) return;
    setTwoFactorStep(devStepOverride);
    if (devStepOverride === "backup-codes" || devStepOverride === "regenerated-codes") {
      setBackupCodes([
        "abcd-efgh-1234",
        "wxyz-mnop-5678",
        "qrst-uvwx-9012",
        "ijkl-mnop-3456",
        "abcd-wxyz-7890",
        "efgh-ijkl-1234",
        "mnop-qrst-5678",
        "uvwx-abcd-9012",
      ]);
      setTotpURI("otpauth://totp/Visor:dev@example.com?secret=DEVMODESECRET&issuer=Visor");
      setTotpSecret("DEVMODESECRET");
    }
    if (devStepOverride === "verify") {
      setTotpURI("otpauth://totp/Visor:dev@example.com?secret=DEVMODESECRET&issuer=Visor");
      setTotpSecret("DEVMODESECRET");
    }
  }, [devStepOverride]);

  // Redirect if not authenticated
  useEffect(() => {
    if (devBypassActive) return;
    if (!authLoading && !user) {
      navigate({ to: "/login", search: { redirect_uri } });
    }
  }, [user, authLoading, navigate, devBypassActive, redirect_uri]);

  // AUTH-123: When the user came in via setup/recovery and AUTH-80 is now
  // satisfied (≥1 method enrolled, no flow in progress), auto-redirect to
  // the destination instead of showing a "Continue" button.
  //
  // AUTH-130: This MUST mirror the canonical predicate in
  // `api/src/services/jwt.service.ts` (`userHasSecondFactor`).
  // Definition: TOTP enrolled OR ≥1 passkey. Phone-verified alone does NOT
  // count — counting it caused the AUTH-124 redirect loop (UI thought the
  // user had a method, server's createJWT gate disagreed, ping-pong).
  // If you change this, change the server too.
  const hasAnyMethod = twoFactorEnabled || passkeys.length > 0;
  const hasTotpFactor = twoFactorEnabled && otpDeliveryMethod === null;
  const hasEmailOtpFactor = twoFactorEnabled && otpDeliveryMethod === "EMAIL";
  const hasSmsOtpFactor = twoFactorEnabled && otpDeliveryMethod === "SMS";
  // AUTH-124: validate redirect_uri before auto-redirecting. Drop unsafe
  // values silently. Only auto-redirect when we have an *explicit* safe
  // redirect_uri — otherwise show the signed-in panel (with its "Go to app"
  // link). Never fall back to "/", which causes a loop with index.tsx's own
  // setup-gate via the 1-minute Better Auth session cookie cache after setup
  // completes.
  const safeRedirectUri = safePostAuthRedirect(redirect_uri, runtimeConfig) ?? undefined;
  const autoRedirectDestination =
    (setup || recovery) && hasAnyMethod && methodInFlow === null && migrationStep === "idle"
      ? safeRedirectUri || null
      : null;

  async function navigateToDestination(destination: string): Promise<void> {
    // `destination` is already resolved (a safe redirect_uri);
    // the shared helper applies the AUTH-154 duplicate check before leaving the
    // gateway. This page can't route through `/` (setup-gate loop, see above),
    // so the inline check in the helper is what surfaces the warning here.
    await navigateToFinalDestination({
      target: destination,
      navigate,
      runtimeConfig,
    });
  }

  useEffect(() => {
    if (!autoRedirectDestination) return;
    if (devBypassActive) return;
    void navigateToDestination(autoRedirectDestination);
  }, [autoRedirectDestination, devBypassActive]);

  // AUTH-80: Verification state — user must verify 2FA or passkey before proceeding
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  // AUTH-104: Trust-device opt-in. Shown on the verification gate and the
  // first-time setup Continue button. Call auth.rememberDevice() after MFA
  // success to upgrade the session cookie to 30-day persistence.
  const [trustDevice, setTrustDevice] = useState(false);

  // Load initial data
  useEffect(() => {
    if (!user) return;
    // AUTH-122: When previewing under bypass, skip the real network calls
    // that would 401 against an unauthenticated session and instead leave
    // the page at sensible defaults (no passkeys, no 2FA).
    if (devBypassActive) {
      setTwoFactorEnabled(false);
      setPasskeys([]);
      setPasskeysLoading(false);
      setSecurityStateLoading(false);
      return;
    }

    const userPhone = (user as { phoneNumber?: string | null }).phoneNumber ?? "";
    setPhoneInput(e164ToDisplay(userPhone));
    let cancelled = false;
    setSecurityStateLoading(true);
    void Promise.all([
      orpcClient.auth.getSession().catch(() => null),
      orpcClient.auth.getOtpDeliveryStatus().catch(() => null),
    ])
      .then(([session, otpStatus]) => {
        if (cancelled) return;

        // After recovery code login, the server hook already disabled 2FA but
        // the Better Auth session user may still be stale. Trust the recovery
        // flag, otherwise use our DB-backed session endpoint.
        const has2FA = recovery ? false : !!session?.user.twoFactorEnabled;
        setTwoFactorEnabled(has2FA);

        if (otpStatus) {
          setEmailVerified(otpStatus.emailAvailable);
          setPhoneVerified(otpStatus.smsAvailable);
          setOtpDeliveryMethodState(otpStatus.method);
          setMigrationConfirmation(otpStatus.migrationConfirmation);
          return;
        }

        setEmailVerified(!!(user as { emailVerified?: boolean }).emailVerified);
        setPhoneVerified(!!(user as { phoneNumberVerified?: boolean }).phoneNumberVerified);
        setOtpDeliveryMethodState(null);
        setMigrationConfirmation(null);
      })
      .finally(() => {
        if (!cancelled) setSecurityStateLoading(false);
      });
    loadPasskeys();

    // AUTH-80: If arriving via setup=true and user already has 2FA,
    // show verification step instead of setup UI.
    return () => {
      cancelled = true;
    };
  }, [user, devBypassActive]);

  const clearSecuritySetupFlag = useCallback(async () => {
    try {
      await orpcClient.auth.clearSecuritySetupFlag();
    } catch {
      // Non-critical — flag will be stale but won't block
    }
  }, []);

  async function handleEnableTwoFactor() {
    try {
      setError(null);
      setLoading(true);
      setTwoFactorStep("enabling");

      const data = await orpcClient.auth.enableTwoFactorEnrollment({ method: "TOTP" });
      if (!data.totpURI) throw new Error("Failed to prepare authenticator setup");

      setTotpURI(data.totpURI);
      setBackupCodes(data.backupCodes);
      setOtpDeliveryMethodState(null);
      const secretMatch = data.totpURI.match(/secret=([^&]+)/);
      setTotpSecret(secretMatch ? secretMatch[1] : "");
      setTwoFactorStep("verify");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enable 2FA");
      setTwoFactorStep("idle");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyAndFinalize() {
    try {
      setError(null);
      setLoading(true);

      const { error: verifyError } = await authClient.twoFactor.verifyTotp({
        code: verifyCode,
      });

      if (verifyError) {
        setError(verifyError.message || "Invalid verification code");
        return;
      }

      setTwoFactorEnabled(true);
      setTwoFactorStep("backup-codes");
      setSuccess("Authenticator app is on.");

      // If this was a recovery re-setup, clear the session flag
      if (recovery) {
        await clearSecuritySetupFlag();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleDisableTwoFactor() {
    try {
      setError(null);
      setLoading(true);

      const { error: disableError } = await authClient.twoFactor.disable({
        password: disablePassword,
      });

      if (disableError) {
        // AUTH-124: server requires step-up before disable. Same retry
        // pattern as phone change.
        if (isStepUpError(disableError)) {
          setLoading(false);
          requestStepUp("Confirm your identity to disable 2FA.", handleDisableTwoFactor);
          return;
        }
        setError(disableError.message || "Failed to disable 2FA");
        return;
      }

      setTwoFactorEnabled(false);
      setTwoFactorStep("idle");
      setDisablePassword("");
      setSuccess("Authenticator app removed.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disable 2FA");
    } finally {
      setLoading(false);
    }
  }

  // AUTH-124: step-up helpers.
  function isStepUpError(err: { code?: string; status?: number } | null): boolean {
    if (!err) return false;
    return err.code === "STEP_UP_REQUIRED" || err.status === 403;
  }

  function requestStepUp(reason: string, retry: () => Promise<void> | void) {
    setStepUpPending({ reason, retry });
    setStepUpPassword("");
    // Lazy-load whether the user has a password — drives which step-up
    // affordance(s) to show in the modal.
    if (stepUpHasPassword === null) {
      orpcClient.auth
        .hasPassword()
        .then((d) => setStepUpHasPassword(d.hasPassword))
        .catch(() => setStepUpHasPassword(false));
    }
  }

  async function handleStepUpWithPassword() {
    if (!stepUpPending) return;
    if (!stepUpPassword) {
      setError("Enter your password");
      return;
    }
    try {
      setError(null);
      setStepUpLoading(true);
      await orpcClient.auth.stepUpWithPassword({ password: stepUpPassword });
      const retry = stepUpPending.retry;
      setStepUpPending(null);
      setStepUpPassword("");
      await retry();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not confirm identity");
    } finally {
      setStepUpLoading(false);
    }
  }

  async function handleStepUpWithPasskey() {
    if (!stepUpPending) return;
    try {
      setError(null);
      setStepUpLoading(true);
      // Better Auth's signIn.passkey() creates a fresh session, which the
      // server treats as elevated (session.createdAt within STEP_UP_WINDOW).
      await preparePendingInvitationAuth(undefined);
      const { error: pkError } = await authClient.signIn.passkey();
      if (pkError) {
        setError(classifyPasskeyError(pkError).message);
        return;
      }
      const retry = stepUpPending.retry;
      setStepUpPending(null);
      await retry();
    } catch (err) {
      setError(classifyPasskeyError(err).message);
    } finally {
      setStepUpLoading(false);
    }
  }

  async function handleRegenerateBackupCodes() {
    try {
      setError(null);
      setLoading(true);

      const { data, error: regenError } = await authClient.twoFactor.generateBackupCodes({
        password: regenPassword,
      });

      if (regenError) {
        setError(regenError.message || "Failed to regenerate backup codes");
        return;
      }

      if (data?.backupCodes) {
        setBackupCodes(data.backupCodes);
        setTwoFactorStep("regenerated-codes");
        setRegenPassword("");
        setSuccess("New recovery codes ready. Old ones won't work anymore.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to regenerate backup codes");
    } finally {
      setLoading(false);
    }
  }

  function copyBackupCodes() {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setSuccess("Recovery codes copied.");
  }

  function downloadBackupCodes() {
    const blob = new Blob([backupCodes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  // Passkey functions
  async function loadPasskeys() {
    try {
      setPasskeysLoading(true);
      const loaded = await orpcClient.auth.listPasskeys();
      setPasskeys(loaded);

      // AUTH-80: If user has passkeys (but no 2FA) and came via setup=true,
      // show verification step — they need to prove they have the passkey.
      if (setup && !recovery && !twoFactorEnabled && loaded.length > 0) {
        setNeedsVerification(true);
      }
    } catch {
      setPasskeys([]);
    } finally {
      setPasskeysLoading(false);
    }
  }

  async function handleRegisterPasskey() {
    try {
      setRegisterLoading(true);
      setError(null);
      setSuccess(null);

      const { error: regError } = await authClient.passkey.addPasskey({
        name: passkeyName || undefined,
      });

      if (regError) {
        setError(regError.message || "Failed to register passkey");
        return;
      }

      setSuccess("Passkey added.");
      setPasskeyName("");
      setMethodInFlow(null);
      await loadPasskeys();

      // If this was a recovery/setup flow and passkey was the chosen method, clear flag
      if (recovery || setup) {
        await clearSecuritySetupFlag();
      }
    } catch (err) {
      setError(classifyPasskeyError(err).message);
    } finally {
      setRegisterLoading(false);
    }
  }

  async function handleDeletePasskey(id: string) {
    try {
      setError(null);
      setSuccess(null);
      await orpcClient.auth.deletePasskey({ id });
      setSuccess("Passkey removed.");
      await loadPasskeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete passkey");
    }
  }

  // Phone / SMS-2FA handlers (AUTH-123)
  async function handleSendPhoneCode() {
    const phoneNumber = displayToE164(phoneInput);
    if (!phoneNumber) {
      setError("Enter a valid US phone number");
      return;
    }
    try {
      setError(null);
      setSuccess(null);
      setPhoneLoading(true);
      const { error: sendError } = await authClient.phoneNumber.sendOtp({ phoneNumber });
      if (sendError) {
        setError(sendError.message || "Failed to send code");
        return;
      }
      setPhoneStep("code-sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send code");
    } finally {
      setPhoneLoading(false);
    }
  }

  // AUTH-123: complete the 2FA-enable side of phone setup. Extracted so
  // recovery from a partial state (phone verified server-side, 2FA never
  // enabled because the original enable call failed) can run it without
  // re-running the verify-OTP step. Walks the user through backup codes
  // on success.
  async function completePhoneTwoFactorEnable(): Promise<{ ok: boolean; error?: string }> {
    try {
      const data = await orpcClient.auth.enableTwoFactorEnrollment({ method: "SMS" });
      setBackupCodes(data.backupCodes);
      setTotpURI(data.totpURI ?? "");
      setTotpSecret("");
      setTwoFactorEnabled(true);
      setOtpDeliveryMethodState("SMS");
      setPhoneStep("backup-codes");
      return { ok: true };
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : "Failed to enable 2FA",
      };
    }
  }

  // AUTH-128: email-OTP setup. First-time enrollment proves code delivery
  // before enabling 2FA; existing 2FA users can switch the delivery
  // preference after the normal authenticated settings flow.
  async function handleEnableEmailOtp() {
    try {
      setError(null);
      setSuccess(null);
      setOtpMethodLoading(true);
      if (!twoFactorEnabled) {
        await orpcClient.auth.startEmailOtpEnrollment();
        setEmailOtpCode("");
        setEmailOtpStep("code-sent");
        return;
      }
      // Persist the delivery preference. Server validates emailVerified.
      await orpcClient.auth.setOtpDeliveryMethod({ method: "EMAIL" });
      setOtpDeliveryMethodState("EMAIL");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set up email OTP");
    } finally {
      setOtpMethodLoading(false);
    }
  }

  async function handleVerifyEmailOtpEnrollment() {
    if (emailOtpCode.length !== 6) {
      setError("Enter the 6-digit code");
      return;
    }
    try {
      setError(null);
      setSuccess(null);
      setOtpMethodLoading(true);
      const data = await orpcClient.auth.completeEmailOtpEnrollment({ code: emailOtpCode });
      setBackupCodes(data.backupCodes);
      setTotpURI(data.totpURI ?? "");
      setTotpSecret("");
      setTwoFactorEnabled(true);
      setOtpDeliveryMethodState("EMAIL");
      setEmailOtpCode("");
      setEmailOtpStep("backup-codes");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to verify email code");
    } finally {
      setOtpMethodLoading(false);
    }
  }

  async function handleSendMigratedOtpCode() {
    try {
      setError(null);
      setSuccess(null);
      setMigrationLoading(true);
      const data = await orpcClient.auth.startMigratedOtpEnrollment();
      setMigrationConfirmation(data);
      setMigrationCode("");
      setMigrationStep("code-sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send code");
    } finally {
      setMigrationLoading(false);
    }
  }

  async function handleVerifyMigratedOtpCode() {
    if (migrationCode.length !== 6) {
      setError("Enter the 6-digit code");
      return;
    }
    try {
      setError(null);
      setSuccess(null);
      setMigrationLoading(true);
      const data = await orpcClient.auth.completeMigratedOtpEnrollment({ code: migrationCode });
      setBackupCodes(data.backupCodes);
      setTotpURI(data.totpURI ?? "");
      setTotpSecret("");
      setTwoFactorEnabled(true);
      setOtpDeliveryMethodState(data.method);
      setMigrationCode("");
      setMigrationStep("backup-codes");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to verify code");
    } finally {
      setMigrationLoading(false);
    }
  }

  // AUTH-128: send a fresh verification email with the original redirect_uri
  // threaded through `callbackURL` so users land back at /security after
  // verifying — preserving the consuming-app handoff if there was one.
  async function handleSendEmailVerification() {
    if (!user) return;
    try {
      setError(null);
      setSuccess(null);
      setOtpMethodLoading(true);
      const prepared = await preparePendingInvitationAuth(undefined);
      const callbackURL = buildEnrollMfaCallbackURL(
        runtimeConfig,
        redirect_uri,
        prepared.continuationId,
      );
      const { error: sendError } = await authClient.sendVerificationEmail({
        email: user.email,
        callbackURL,
      });
      if (sendError) {
        setError(sendError.message ?? "Failed to send verification email");
        return;
      }
      setSuccess("Verification email sent. Click the link, then come back here.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send verification email");
    } finally {
      setOtpMethodLoading(false);
    }
  }

  // AUTH-128: switch the delivery preference from manage view (e.g. user
  // has both phone + email verified and wants to flip channels). Server
  // validates eligibility.
  async function handleSetOtpDeliveryMethod(method: "SMS" | "EMAIL") {
    try {
      setError(null);
      setSuccess(null);
      setOtpMethodLoading(true);
      await orpcClient.auth.setOtpDeliveryMethod({ method });
      setOtpDeliveryMethodState(method);
      setSuccess(
        method === "SMS"
          ? "OTP codes will be sent via SMS."
          : "OTP codes will be sent to your email.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to switch delivery method");
    } finally {
      setOtpMethodLoading(false);
    }
  }

  async function handleVerifyPhoneCode() {
    const phoneNumber = displayToE164(phoneInput);
    if (!phoneNumber) {
      setError("Enter a valid US phone number");
      return;
    }
    if (!phoneCode || phoneCode.length !== 6) {
      setError("Enter the 6-digit code");
      return;
    }
    try {
      setError(null);
      setSuccess(null);
      setPhoneLoading(true);
      const { error: verifyError } = await authClient.phoneNumber.verify({
        phoneNumber,
        code: phoneCode,
        // Link the verified number to the session user. Without this,
        // Better Auth treats verify as a sign-in by phone (looks up an
        // existing user with that number) and 500s when none exists.
        updatePhoneNumber: true,
      });
      if (verifyError) {
        // AUTH-124: server requires step-up for phone CHANGES. Surface the
        // modal and queue the retry — the user re-enters their password (or
        // re-signs-in with passkey), which elevates the session, then the
        // verify retries automatically.
        if (isStepUpError(verifyError)) {
          setPhoneLoading(false);
          requestStepUp(
            "Confirm your identity to change your phone number.",
            handleVerifyPhoneCode,
          );
          return;
        }
        const code = (verifyError as { code?: string }).code;
        if (code === "PHONE_NUMBER_EXIST") {
          setPhoneVerified(false);
          setPhoneStep("idle");
          setPhoneCode("");
          setError("That phone number is already used by another account.");
          return;
        }
        setError(verifyError.message || "Invalid code");
        return;
      }
      setPhoneVerified(true);
      setPhoneCode("");

      // AUTH-123: phone verification alone doesn't satisfy AUTH-80 — Better
      // Auth's twoFactor plugin still needs to be enabled for SMS to actually
      // challenge at sign-in. If 2FA isn't on yet, enable it now and walk
      // the user through backup codes — they're recovery if SMS is
      // unreachable.
      if (!twoFactorEnabled) {
        const result = await completePhoneTwoFactorEnable();
        if (!result.ok) {
          setError(result.error ?? "Failed to finish setting up 2FA");
        }
        return;
      }

      // Already enabled (e.g. user is changing their phone number) — exit.
      setPhoneStep("idle");
      setMethodInFlow(null);
      setSuccess("Phone number verified.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to verify code");
    } finally {
      setPhoneLoading(false);
    }
  }

  if (authLoading) {
    return (
      <div className="flex justify-center py-8" role="status" data-testid="security-auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) return null;

  // AUTH-96: When arriving via setup=true, don't render the settings panel
  // until we know whether verification is needed. Without this guard, the
  // settings panel flashes briefly before the async passkey check resolves
  // and switches to the verification panel.
  if (setup && !recovery && passkeysLoading && !needsVerification) {
    return (
      <div
        className="flex items-center justify-center min-h-[200px]"
        role="status"
        data-testid="security-setup-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // AUTH-80: Verification gate — user must prove second factor before proceeding
  if (needsVerification) {
    // AUTH-124: validate redirect_uri before navigating off-origin. Drop
    // unsafe values silently — falling back to the existing "stay on this
    // page" UX (setNeedsVerification(false)).
    const safeUri = safePostAuthRedirect(redirect_uri, runtimeConfig) ?? undefined;
    const destination = safeUri || undefined;

    async function handleVerifyTotp() {
      try {
        setError(null);
        setVerificationLoading(true);
        // AUTH-104: pass trustDevice natively to Better Auth's twoFactor plugin.
        // Sets the `better-auth.trust_device` cookie (30d), clears `dont_remember`,
        // and skips the 2FA challenge on the next sign-in (industry norm).
        const { error: verifyError } = await authClient.twoFactor.verifyTotp({
          code: verificationCode,
          trustDevice,
        });
        if (verifyError) {
          setError(verifyError.message || "Invalid verification code");
          return;
        }
        // Verified — proceed to app
        if (destination) {
          void navigateToDestination(destination);
        } else {
          setNeedsVerification(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verification failed");
      } finally {
        setVerificationLoading(false);
      }
    }

    async function handleVerifyPasskey() {
      try {
        setError(null);
        setVerificationLoading(true);
        // AUTH-104: passkey is strong device binding — no separate trust-device
        // opt-in needed. Better Auth's signIn.passkey() issues a persistent
        // session by default on success.
        await preparePendingInvitationAuth(undefined);
        const { error: pkError } = await authClient.signIn.passkey();
        if (pkError) {
          setError(classifyPasskeyError(pkError).message);
          return;
        }
        if (destination) {
          void navigateToDestination(destination);
        } else {
          setNeedsVerification(false);
        }
      } catch (err) {
        setError(classifyPasskeyError(err).message);
      } finally {
        setVerificationLoading(false);
      }
    }

    return (
      <div className="space-y-8">
        <div className="space-y-1.5">
          <h2 className="text-2xl font-semibold tracking-tight" data-testid="security-verify-title">
            Confirm it's you
          </h2>
          <p className="text-sm text-muted-foreground">
            One more check before we let you change security settings.
          </p>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="security-verify-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* TOTP verification */}
        {hasTotpFactor && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-base font-semibold">
              <ShieldCheck className="h-4 w-4" />
              Authenticator app
            </div>
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code from your authenticator app.
            </p>
            <OtpDigits
              value={verificationCode}
              onChange={setVerificationCode}
              data-testid="security-verify-totp-code-input"
            />
            {/* AUTH-104: trust-device opt-in scoped to TOTP panel — passkey
                panel below doesn't offer this because passkey is inherently
                strong device binding. */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="trust-device-totp"
                checked={trustDevice}
                onCheckedChange={(v) => setTrustDevice(v === true)}
                data-testid="security-verify-trust-device-checkbox"
              />
              <Label htmlFor="trust-device-totp" className="text-sm font-normal cursor-pointer">
                Trust this device for 30 days
              </Label>
            </div>
            <Button
              onClick={handleVerifyTotp}
              disabled={verificationCode.length !== 6 || verificationLoading}
              className="w-full"
              data-testid="security-verify-totp-submit-button"
            >
              {verificationLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying…
                </>
              ) : (
                "Verify"
              )}
            </Button>
          </div>
        )}

        {/* Passkey verification as alternative */}
        {passkeys.length > 0 && (
          <>
            {twoFactorEnabled && <Separator />}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-base font-semibold">
                <Fingerprint className="h-4 w-4" />
                {twoFactorEnabled ? "Or use a passkey" : "Verify with passkey"}
              </div>
              <p className="text-sm text-muted-foreground">
                Use your registered passkey to verify your identity.
              </p>
              <Button
                variant={twoFactorEnabled ? "outline" : "default"}
                onClick={handleVerifyPasskey}
                disabled={verificationLoading}
                className="w-full"
                data-testid="security-verify-passkey-button"
              >
                {verificationLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Waiting for passkey...
                  </>
                ) : (
                  <>
                    <Fingerprint className="mr-2 h-4 w-4" />
                    Verify with passkey
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  const hasFinishedSetupWithoutDestination =
    (setup || recovery) &&
    hasAnyMethod &&
    methodInFlow === null &&
    migrationStep === "idle" &&
    !autoRedirectDestination;

  if (hasFinishedSetupWithoutDestination) {
    return <SignedInPanel data-testid="security-signed-in-panel" />;
  }

  return (
    <div className="space-y-8">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-2xl font-semibold tracking-tight" data-testid="security-title">
            Sign-in security
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={signOut}
            data-testid="security-sign-out-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Signed in as{" "}
          <span className="font-medium text-foreground" data-testid="security-account-email">
            {user.email}
          </span>
        </p>
      </div>

      {/* Recovery banner */}
      {recovery && (
        <Alert variant="warning" data-testid="security-recovery-banner">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>You're back in.</AlertTitle>
          <AlertDescription>
            We cleared your old security method as a precaution. Set up a fresh one below to keep
            your account secure.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" data-testid="security-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="success" data-testid="security-success">
          <ShieldCheck className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* AUTH-123 redesign: derive the rendered mode from data.
            - showPicker → no methods + not currently configuring one
            - showFlow   → currently configuring a specific method
            - showManage → ≥1 method enrolled and not currently configuring one
       */}
      {(() => {
        // `phoneVerified` is included defensively — the verify handler also
        // auto-enables 2FA, so `twoFactorEnabled` should already be true in
        // the happy path, but stale state shouldn't drop the user back into
        // the picker.
        const showFlow = methodInFlow !== null;
        const showMigrationConfirmation =
          !securityStateLoading &&
          !showFlow &&
          !!migrationConfirmation &&
          !recovery &&
          (!hasAnyMethod || migrationStep !== "idle");
        const showPicker =
          !securityStateLoading && !showFlow && !hasAnyMethod && !showMigrationConfirmation;
        // While the auto-redirect effect is firing the manage view would
        // briefly flash before navigation — render a simple spinner instead.
        const showManage =
          !securityStateLoading &&
          !showFlow &&
          hasAnyMethod &&
          !autoRedirectDestination &&
          migrationStep === "idle";

        function startMethod(method: MethodKey) {
          setError(null);
          setSuccess(null);
          setMethodInFlow(method);
          if (method === "totp") {
            // TOTP enable is async; kick it off so the user lands on the
            // QR-code step instead of a blank "starting..." flash.
            void handleEnableTwoFactor();
          }
          if (method === "phone") {
            setPhoneStep("idle");
            setPhoneCode("");
          }
          if (method === "email") {
            setEmailOtpStep("idle");
            setEmailOtpCode("");
            if (emailVerified) {
              void handleEnableEmailOtp();
            }
          }
        }

        function exitFlow() {
          setMethodInFlow(null);
          // Wipe any partial sub-flow state so re-entering starts clean.
          if (twoFactorStep !== "idle") {
            setTwoFactorStep("idle");
            setTotpURI("");
            setTotpSecret("");
            setVerifyCode("");
            setBackupCodes([]);
          }
          if (phoneStep === "code-sent" && !phoneVerified) {
            setPhoneStep("idle");
            setPhoneCode("");
          }
          if (emailOtpStep === "code-sent") {
            setEmailOtpStep("idle");
            setEmailOtpCode("");
          }
        }

        return (
          <>
            {/* ── Auto-redirect spinner: in setup/recovery flow with a known
                  destination, AUTH-80 is now satisfied and the user is being
                  forwarded to the originating app. No Continue button — their
                  intent was already to return. */}
            {autoRedirectDestination && (
              <div
                className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground"
                role="status"
                data-testid="security-redirecting-loading"
              >
                <Loader2 className="h-6 w-6 animate-spin" />
                <p className="text-sm">Returning you to the app…</p>
              </div>
            )}

            {securityStateLoading && !showFlow && !autoRedirectDestination && (
              <div
                className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground"
                role="status"
                data-testid="security-state-loading"
              >
                <Loader2 className="h-6 w-6 animate-spin" />
                <p className="text-sm">Loading security settings…</p>
              </div>
            )}

            {showMigrationConfirmation && (
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <h3 className="text-lg font-semibold tracking-tight">
                    Confirm your sign-in security
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    We upgraded our sign-in system. Confirm your existing{" "}
                    {migrationConfirmation.method === "SMS" ? "SMS" : "email"} code method to finish
                    the move.
                  </p>
                </div>

                {migrationStep === "idle" && (
                  <Button
                    type="button"
                    onClick={handleSendMigratedOtpCode}
                    disabled={migrationLoading}
                    className="w-full"
                    data-testid="security-migration-send-code-button"
                  >
                    {migrationLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        {migrationConfirmation.method === "SMS" ? (
                          <Smartphone className="mr-2 h-4 w-4" />
                        ) : (
                          <Mail className="mr-2 h-4 w-4" />
                        )}
                        Send code to{" "}
                        {migrationConfirmation.method === "SMS"
                          ? maskPhoneLastFour(migrationConfirmation.target)
                          : migrationConfirmation.target}
                      </>
                    )}
                  </Button>
                )}

                {migrationStep === "code-sent" && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Enter the 6-digit code sent to{" "}
                      <span className="font-medium text-foreground">
                        {migrationConfirmation.method === "SMS"
                          ? maskPhoneLastFour(migrationConfirmation.target)
                          : migrationConfirmation.target}
                      </span>
                      .
                    </p>
                    <OtpDigits
                      value={migrationCode}
                      onChange={setMigrationCode}
                      data-testid="security-migration-code-input"
                    />
                    <Button
                      onClick={handleVerifyMigratedOtpCode}
                      disabled={migrationLoading || migrationCode.length !== 6}
                      className="w-full"
                      data-testid="security-migration-verify-button"
                    >
                      {migrationLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        "Verify code"
                      )}
                    </Button>
                    <button
                      type="button"
                      onClick={handleSendMigratedOtpCode}
                      disabled={migrationLoading}
                      className="block w-full text-center text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                      data-testid="security-migration-resend-button"
                    >
                      Didn't receive a code? Resend
                    </button>
                  </div>
                )}

                {migrationStep === "backup-codes" && backupCodes.length > 0 && (
                  <div className="space-y-4">
                    <Alert variant="warning" data-testid="security-migration-backup-codes-section">
                      <AlertTitle>Save these recovery codes.</AlertTitle>
                      <AlertDescription>
                        You can use one to sign in if you lose access to your{" "}
                        {migrationConfirmation.method === "SMS" ? "phone" : "email"}. Each code
                        works once.
                      </AlertDescription>
                    </Alert>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-muted/60 p-4">
                      {backupCodes.map((code, i) => (
                        <code key={i} className="text-sm font-mono text-center py-1">
                          {code}
                        </code>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={copyBackupCodes}
                        className="flex-1"
                        data-testid="security-migration-backup-codes-copy-button"
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copy
                      </Button>
                      <Button
                        variant="outline"
                        onClick={downloadBackupCodes}
                        className="flex-1"
                        data-testid="security-migration-backup-codes-download-button"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    </div>
                    <Button
                      data-testid="security-migration-backup-codes-done-button"
                      onClick={() => {
                        setBackupCodes([]);
                        setTotpURI("");
                        setTotpSecret("");
                        setMigrationStep("idle");
                        setMigrationConfirmation(null);
                        setSuccess(
                          migrationConfirmation.method === "SMS"
                            ? "SMS codes are on."
                            : "Email codes are on.",
                        );
                      }}
                      className="w-full"
                    >
                      Done
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* ── PICKER ── */}
            {showPicker && (
              <div className="space-y-6">
                <div className="space-y-1.5">
                  <h3 className="text-lg font-semibold tracking-tight">
                    {recovery ? "Set up a new security method" : "Pick a security method"}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {recovery
                      ? "Your old one is gone. This locks the front door again."
                      : "Set this up once. After that, signing in stays quick."}
                  </p>
                </div>

                {/* Featured: passkey — uses electric semantic family for the
                    "highlighted/notable" surface that distinguishes it from the
                    default card surface in the alternates row. */}
                <button
                  type="button"
                  onClick={() => startMethod("passkey")}
                  data-testid="security-method-passkey-button"
                  className="block w-full rounded-lg border border-electric bg-electric-subtle p-5 text-left transition-colors hover:bg-electric/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-electric"
                >
                  <div className="flex items-start gap-4">
                    <Fingerprint className="mt-0.5 h-6 w-6 shrink-0 text-electric" />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-electric-ink">Passkey</span>
                        <Badge variant="electric" className="text-[10px] uppercase tracking-wider">
                          Recommended
                        </Badge>
                      </div>
                      <p className="text-sm text-electric-ink/80">
                        Fingerprint, face, or device PIN. Phishing-resistant. Nothing to type.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Tier 2: Authenticator app — full-width, neutral surface
                      sits between the featured passkey card and the smaller
                      paired email/SMS cards below. Stronger than Tier 3 but
                      visually subordinate to Tier 1. */}
                <button
                  type="button"
                  onClick={() => startMethod("totp")}
                  data-testid="security-method-authenticator-button"
                  className="block w-full rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="flex-1 space-y-1">
                      <span className="text-sm font-semibold">Authenticator app</span>
                      <p className="text-xs text-muted-foreground">
                        6-digit codes from an app on your device — 1Password, Authy, Google
                        Authenticator.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Tier 3: Email + SMS — server-delivered codes, weaker
                      against email/SIM compromise. Smallest cards. */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => startMethod("email")}
                    data-testid="security-method-email-button"
                    className="block rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="min-w-0 space-y-1.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="text-sm font-semibold">Email</span>
                      </div>
                      <p className="[overflow-wrap:anywhere] text-xs text-muted-foreground">
                        Codes sent to {user?.email ?? "your email"}.
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => startMethod("phone")}
                    data-testid="security-method-phone-button"
                    className="block rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Smartphone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-semibold">Phone (SMS)</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Codes texted to your phone.</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* ── FLOW: back link sits above the existing per-method UI.
                  Suppressed during backup-codes display so users don't bail
                  before saving recovery codes. */}
            {showFlow &&
              twoFactorStep !== "backup-codes" &&
              phoneStep !== "backup-codes" &&
              hasAnyMethod && (
                <button
                  type="button"
                  onClick={exitFlow}
                  className="text-sm text-muted-foreground hover:text-foreground"
                  data-testid="security-back-to-methods-button"
                >
                  ← Back to security methods
                </button>
              )}
            {showFlow &&
              twoFactorStep !== "backup-codes" &&
              phoneStep !== "backup-codes" &&
              !hasAnyMethod && (
                <button
                  type="button"
                  onClick={exitFlow}
                  className="text-sm text-muted-foreground hover:text-foreground"
                  data-testid="security-pick-different-method-button"
                >
                  ← Pick a different method
                </button>
              )}

            {/* ── MANAGE: compact list of enrolled methods + add another ── */}
            {showManage && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-base font-semibold">Sign-in security</h3>
                </div>

                <div className="space-y-2">
                  {hasTotpFactor && (
                    <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <ShieldCheck className="h-5 w-5 shrink-0 text-success" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">Authenticator app</p>
                          <p className="text-xs text-muted-foreground">
                            6-digit codes from your authenticator
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setTwoFactorStep("regenerating-codes")}
                          data-testid="security-totp-new-codes-button"
                        >
                          <RefreshCw className="mr-1 h-3.5 w-3.5" />
                          New codes
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (passkeys.length === 0 && !phoneVerified) {
                              setError(
                                "Add another security method before removing your authenticator.",
                              );
                              return;
                            }
                            setTwoFactorStep("disabling");
                          }}
                          data-testid="security-totp-disable-button"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {hasEmailOtpFactor && (
                    <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Mail className="h-5 w-5 shrink-0 text-success" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">Email codes</p>
                          <p className="text-xs text-muted-foreground">Codes emailed at sign-in</p>
                        </div>
                      </div>
                      {phoneVerified && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetOtpDeliveryMethod("SMS")}
                          disabled={otpMethodLoading}
                          data-testid="security-email-use-sms-button"
                        >
                          Use SMS
                        </Button>
                      )}
                    </div>
                  )}

                  {hasSmsOtpFactor && (
                    <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Smartphone className="h-5 w-5 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            SMS · {e164ToDisplay(phoneInput) || phoneInput}
                          </p>
                          <p className="text-xs text-muted-foreground">Codes texted at sign-in</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {emailVerified && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetOtpDeliveryMethod("EMAIL")}
                            disabled={otpMethodLoading}
                            data-testid="security-sms-use-email-button"
                          >
                            Use email
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startMethod("phone")}
                          data-testid="security-sms-change-button"
                        >
                          Change
                        </Button>
                      </div>
                    </div>
                  )}

                  {passkeys.map((pk) => (
                    <div
                      key={pk.id}
                      className="flex items-center justify-between gap-3 rounded-md border p-3"
                      data-testid={`security-passkey-row-${pk.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Fingerprint className="h-5 w-5 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{pk.name || "Passkey"}</p>
                          <p className="text-xs text-muted-foreground">
                            {pk.deviceType} · added {new Date(pk.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePasskey(pk.id)}
                        data-testid={`security-passkey-delete-${pk.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* AUTH-128: delivery-method toggle. Shown when the user
                    has both phone and email available — flips which channel
                    receives OTPs at next sign-in. Hidden if only one channel
                    is configured (the option is the only one). */}
                {hasTotpFactor && phoneVerified && emailVerified && (
                  <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                    <div className="flex items-start gap-2">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">OTP delivery</p>
                        <p className="text-xs text-muted-foreground">
                          Where to send your code at sign-in (in addition to your authenticator app,
                          if any).
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant={otpDeliveryMethod === "EMAIL" ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleSetOtpDeliveryMethod("EMAIL")}
                        disabled={otpMethodLoading}
                        className="flex-1"
                        data-testid="security-otp-delivery-email-button"
                      >
                        <Mail className="mr-1.5 h-3.5 w-3.5" />
                        Email
                      </Button>
                      <Button
                        variant={otpDeliveryMethod === "SMS" ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleSetOtpDeliveryMethod("SMS")}
                        disabled={otpMethodLoading}
                        className="flex-1"
                        data-testid="security-otp-delivery-sms-button"
                      >
                        <Smartphone className="mr-1.5 h-3.5 w-3.5" />
                        SMS
                      </Button>
                    </div>
                  </div>
                )}

                {/* Add another method — compact menu of unconfigured options */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {!hasTotpFactor && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startMethod("totp")}
                      data-testid="security-add-authenticator-button"
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      Authenticator app
                    </Button>
                  )}
                  {!hasSmsOtpFactor && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startMethod("phone")}
                      data-testid="security-add-sms-button"
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      SMS
                    </Button>
                  )}
                  {!hasEmailOtpFactor && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startMethod("email")}
                      data-testid="security-add-email-button"
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      Email
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startMethod("passkey")}
                    data-testid="security-add-passkey-button"
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Passkey
                  </Button>
                </div>

                {/* Inline TOTP destructive sub-flows (regenerate / disable)
                    rendered here so the user stays in the manage view. */}
                {twoFactorStep === "regenerating-codes" && (
                  <div className="space-y-3 rounded-md border bg-muted/30 p-4">
                    <p className="text-sm text-muted-foreground">
                      Enter your password to generate new backup codes. All previous codes will be
                      invalidated.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="regen-password">Password</Label>
                      <PasswordInput
                        id="regen-password"
                        value={regenPassword}
                        onChange={(e) => setRegenPassword(e.target.value)}
                        placeholder="Enter your password"
                        autoFocus
                        data-testid="security-regen-password-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setTwoFactorStep("idle");
                          setRegenPassword("");
                        }}
                        className="flex-1"
                        data-testid="security-regen-cancel-button"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleRegenerateBackupCodes}
                        disabled={!regenPassword || loading}
                        className="flex-1"
                        data-testid="security-regen-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          "Regenerate codes"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
                {twoFactorStep === "regenerated-codes" && (
                  <div
                    className="space-y-4 rounded-md border bg-muted/30 p-4"
                    data-testid="security-regen-backup-codes-section"
                  >
                    <p className="text-sm text-muted-foreground">
                      Your new backup codes are below. Previous codes are no longer valid.
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-muted/60 p-4">
                      {backupCodes.map((code, i) => (
                        <code key={i} className="text-sm font-mono text-center py-1">
                          {code}
                        </code>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={copyBackupCodes}
                        className="flex-1"
                        data-testid="security-regen-backup-codes-copy-button"
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copy
                      </Button>
                      <Button
                        variant="outline"
                        onClick={downloadBackupCodes}
                        className="flex-1"
                        data-testid="security-regen-backup-codes-download-button"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    </div>
                    <Button
                      onClick={() => {
                        setTwoFactorStep("idle");
                        setBackupCodes([]);
                      }}
                      className="w-full"
                      data-testid="security-regen-backup-codes-done-button"
                    >
                      Done
                    </Button>
                  </div>
                )}
                {twoFactorStep === "disabling" && (
                  <div className="space-y-3 rounded-md border bg-muted/30 p-4">
                    <p className="text-sm text-muted-foreground">
                      Enter your password to confirm disabling two-factor authentication.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="disable-password">Password</Label>
                      <PasswordInput
                        id="disable-password"
                        value={disablePassword}
                        onChange={(e) => setDisablePassword(e.target.value)}
                        placeholder="Enter your password"
                        autoFocus
                        data-testid="security-disable-password-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setTwoFactorStep("idle");
                          setDisablePassword("");
                        }}
                        className="flex-1"
                        data-testid="security-disable-cancel-button"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDisableTwoFactor}
                        disabled={!disablePassword || loading}
                        className="flex-1"
                        data-testid="security-disable-confirm-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Disabling...
                          </>
                        ) : (
                          "Confirm disable"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Authenticator (TOTP) flow ── */}
            {showFlow && methodInFlow === "totp" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-base font-semibold">
                  <ShieldCheck className="h-4 w-4" />
                  Set up authenticator app
                </div>

                {twoFactorStep === "enabling" && (
                  <div
                    className="flex justify-center py-6"
                    role="status"
                    data-testid="security-totp-enabling-loading"
                  >
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}

                {twoFactorStep === "verify" && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Scan with an authenticator app, then enter the 6-digit code to confirm.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Don't have one yet? Try{" "}
                      <a
                        href="https://1password.com/downloads"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:text-foreground"
                        data-testid="security-totp-1password-link"
                      >
                        1Password
                      </a>
                      ,{" "}
                      <a
                        href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:text-foreground"
                        data-testid="security-totp-microsoft-link"
                      >
                        Microsoft Authenticator
                      </a>
                      , or{" "}
                      <a
                        href="https://authy.com/download/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:text-foreground"
                        data-testid="security-totp-authy-link"
                      >
                        Authy
                      </a>
                      .
                    </p>

                    <div
                      className="flex justify-center p-4 bg-card rounded-md border"
                      data-testid="security-totp-qr-section"
                    >
                      <QRCodeSVG value={totpURI} size={200} />
                    </div>

                    {totpSecret && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">
                          Can't scan? Enter this key manually:
                        </p>
                        <code
                          className="block text-sm bg-muted p-2 rounded font-mono text-center select-all break-all"
                          data-testid="security-totp-secret"
                        >
                          {totpSecret}
                        </code>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="verify-code" className="block text-center">
                        Enter the code your app shows
                      </Label>
                      <OtpDigits
                        value={verifyCode}
                        onChange={setVerifyCode}
                        data-testid="security-totp-verify-code-input"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setTwoFactorStep("idle");
                          setTotpURI("");
                          setTotpSecret("");
                          setVerifyCode("");
                        }}
                        className="flex-1"
                        data-testid="security-totp-cancel-button"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleVerifyAndFinalize}
                        disabled={verifyCode.length !== 6 || loading}
                        className="flex-1"
                        data-testid="security-totp-verify-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Verifying…
                          </>
                        ) : (
                          "Verify & enable"
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {(twoFactorStep === "backup-codes" || twoFactorStep === "regenerated-codes") && (
                  <div className="space-y-4" data-testid="security-totp-backup-codes-section">
                    <p className="text-sm text-muted-foreground">
                      {twoFactorStep === "regenerated-codes"
                        ? "Your new backup codes are below. Previous codes are no longer valid."
                        : "Save these backup codes in a safe place. You can use each code once to sign in if you lose access to your authenticator app."}
                    </p>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-muted/60 p-4">
                      {backupCodes.map((code, i) => (
                        <code key={i} className="text-sm font-mono text-center py-1">
                          {code}
                        </code>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={copyBackupCodes}
                        className="flex-1"
                        data-testid="security-totp-backup-codes-copy-button"
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copy
                      </Button>
                      <Button
                        variant="outline"
                        onClick={downloadBackupCodes}
                        className="flex-1"
                        data-testid="security-totp-backup-codes-download-button"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    </div>

                    <Button
                      data-testid="security-totp-backup-codes-done-button"
                      onClick={() => {
                        // AUTH-123: backup codes saved → exit the flow back to manage.
                        setTwoFactorStep("idle");
                        setBackupCodes([]);
                        setTotpURI("");
                        setTotpSecret("");
                        setVerifyCode("");
                        setMethodInFlow(null);
                        setSuccess("Authenticator app added.");
                      }}
                      className="w-full"
                    >
                      Done
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* ── Phone / SMS flow ── */}
            {showFlow && methodInFlow === "phone" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-base font-semibold">
                  <Smartphone className="h-4 w-4" />
                  {phoneVerified ? "Change phone number" : "Add phone number"}
                </div>
                <p className="text-sm text-muted-foreground">
                  We'll text a 6-digit code each time you sign in.
                </p>

                <div className="space-y-3">
                  {phoneStep !== "backup-codes" && (
                    <div className="space-y-2">
                      <Label htmlFor="phone-number">Phone number</Label>
                      <MaskedInput
                        id="phone-number"
                        type="tel"
                        mask="(999) 999-9999"
                        placeholder="(555) 123-4567"
                        value={phoneInput}
                        onChange={(e) => {
                          setPhoneInput(e.target.value);
                          if (phoneVerified) setPhoneVerified(false);
                          if (phoneStep === "code-sent") setPhoneStep("idle");
                        }}
                        disabled={phoneStep === "code-sent" || phoneLoading}
                        autoComplete="tel"
                        data-testid="security-phone-number-input"
                      />
                    </div>
                  )}

                  {phoneStep === "idle" && !phoneVerified && (
                    <Button
                      variant="default"
                      onClick={handleSendPhoneCode}
                      disabled={phoneLoading || digitsOnly(phoneInput).length < 10}
                      className="w-full"
                      data-testid="security-phone-send-code-button"
                    >
                      {phoneLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send verification code"
                      )}
                    </Button>
                  )}

                  {phoneStep === "code-sent" && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="phone-code" className="block text-center">
                          Enter the code we just texted
                        </Label>
                        <OtpDigits
                          value={phoneCode}
                          onChange={setPhoneCode}
                          data-testid="security-phone-code-input"
                        />
                      </div>
                      <Button
                        onClick={handleVerifyPhoneCode}
                        disabled={phoneLoading || phoneCode.length !== 6}
                        className="w-full"
                        data-testid="security-phone-verify-button"
                      >
                        {phoneLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          "Verify code"
                        )}
                      </Button>
                      <button
                        type="button"
                        onClick={handleSendPhoneCode}
                        disabled={phoneLoading}
                        className="block w-full text-center text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                        data-testid="security-phone-resend-button"
                      >
                        Didn't receive a code? Resend
                      </button>
                    </div>
                  )}

                  {phoneStep === "backup-codes" && (
                    <div className="space-y-4" data-testid="security-phone-backup-codes-section">
                      <Alert variant="warning">
                        <AlertTitle>Save these recovery codes.</AlertTitle>
                        <AlertDescription>
                          You can use one to sign in if you lose access to your phone. Each code
                          works once.
                        </AlertDescription>
                      </Alert>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-muted/60 p-4">
                        {backupCodes.map((code, i) => (
                          <code key={i} className="text-sm font-mono text-center py-1">
                            {code}
                          </code>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={copyBackupCodes}
                          className="flex-1"
                          data-testid="security-phone-backup-codes-copy-button"
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Copy
                        </Button>
                        <Button
                          variant="outline"
                          onClick={downloadBackupCodes}
                          className="flex-1"
                          data-testid="security-phone-backup-codes-download-button"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </Button>
                      </div>
                      <Button
                        data-testid="security-phone-backup-codes-done-button"
                        onClick={() => {
                          setPhoneStep("idle");
                          setBackupCodes([]);
                          setTotpURI("");
                          setTotpSecret("");
                          setMethodInFlow(null);
                          setSuccess("SMS codes are on.");
                        }}
                        className="w-full"
                      >
                        Done
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Email-OTP flow ── */}
            {showFlow && methodInFlow === "email" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-base font-semibold">
                  <Mail className="h-4 w-4" />
                  {emailVerified ? "Email codes" : "Verify your email"}
                </div>

                {!emailVerified ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Email codes need a verified email. We'll send a verification link to{" "}
                      <span className="font-medium text-foreground">{user?.email}</span>. Click the
                      link, then return here to finish setup.
                    </p>
                    <Button
                      onClick={handleSendEmailVerification}
                      disabled={otpMethodLoading}
                      className="w-full"
                      data-testid="security-email-send-verification-button"
                    >
                      {otpMethodLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send verification email"
                      )}
                    </Button>
                  </>
                ) : emailOtpStep === "code-sent" ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Enter the 6-digit code sent to{" "}
                      <span className="font-medium text-foreground">{user?.email}</span>.
                    </p>
                    <OtpDigits
                      value={emailOtpCode}
                      onChange={setEmailOtpCode}
                      data-testid="security-email-code-input"
                    />
                    <Button
                      onClick={handleVerifyEmailOtpEnrollment}
                      disabled={otpMethodLoading || emailOtpCode.length !== 6}
                      className="w-full"
                      data-testid="security-email-verify-button"
                    >
                      {otpMethodLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        "Verify code"
                      )}
                    </Button>
                    <button
                      type="button"
                      onClick={handleEnableEmailOtp}
                      disabled={otpMethodLoading}
                      className="block w-full text-center text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                      data-testid="security-email-resend-button"
                    >
                      Didn't receive a code? Resend
                    </button>
                  </div>
                ) : emailOtpStep === "backup-codes" && backupCodes.length > 0 ? (
                  <div className="space-y-4" data-testid="security-email-backup-codes-section">
                    <Alert variant="warning">
                      <AlertTitle>Save these recovery codes.</AlertTitle>
                      <AlertDescription>
                        You can use one to sign in if you can't access your email. Each code works
                        once.
                      </AlertDescription>
                    </Alert>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-muted/60 p-4">
                      {backupCodes.map((code, i) => (
                        <code key={i} className="text-sm font-mono text-center py-1">
                          {code}
                        </code>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={copyBackupCodes}
                        className="flex-1"
                        data-testid="security-email-backup-codes-copy-button"
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copy
                      </Button>
                      <Button
                        variant="outline"
                        onClick={downloadBackupCodes}
                        className="flex-1"
                        data-testid="security-email-backup-codes-download-button"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </Button>
                    </div>
                    <Button
                      data-testid="security-email-backup-codes-done-button"
                      onClick={() => {
                        setBackupCodes([]);
                        setTotpURI("");
                        setTotpSecret("");
                        setEmailOtpStep("idle");
                        setMethodInFlow(null);
                        setSuccess("Email codes are on.");
                      }}
                      className="w-full"
                    >
                      Done
                    </Button>
                  </div>
                ) : (
                  <div
                    className="flex flex-col items-center justify-center gap-2 py-6 text-muted-foreground"
                    role="status"
                    data-testid="security-email-sending-loading"
                  >
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <p className="text-xs">Sending email code...</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Passkey flow ── */}
            {showFlow && methodInFlow === "passkey" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-base font-semibold">
                  <Fingerprint className="h-4 w-4" />
                  Add a passkey
                </div>
                <p className="text-sm text-muted-foreground">
                  Sign in with your fingerprint, face, or screen lock. The next step will use your
                  device's built-in prompt.
                </p>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="passkey-name">Name (optional)</Label>
                    <Input
                      id="passkey-name"
                      placeholder="e.g. MacBook Pro, iPhone"
                      value={passkeyName}
                      onChange={(e) => setPasskeyName(e.target.value)}
                      data-testid="security-passkey-name-input"
                    />
                  </div>
                  <Button
                    onClick={handleRegisterPasskey}
                    disabled={registerLoading}
                    className="w-full"
                    data-testid="security-passkey-register-button"
                  >
                    {registerLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Waiting for device...
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2 h-4 w-4" />
                        Register passkey
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* AUTH-124: step-up modal. Surfaces when the server returns
            STEP_UP_REQUIRED on a sensitive op (change phone, disable 2FA).
            Offers password verification (if the user has a password) and/or
            passkey re-sign-in (if they have any registered passkeys).
            Successful step-up auto-retries the original action. */}
      {stepUpPending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="step-up-title"
          data-testid="security-step-up-dialog"
        >
          <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-lg space-y-4">
            <div className="space-y-1.5">
              <h3 id="step-up-title" className="text-lg font-semibold tracking-tight">
                Confirm it's you
              </h3>
              <p className="text-sm text-muted-foreground">{stepUpPending.reason}</p>
            </div>

            {stepUpHasPassword === null ? (
              <div
                className="flex justify-center py-4"
                role="status"
                data-testid="security-step-up-loading"
              >
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {stepUpHasPassword && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void handleStepUpWithPassword();
                    }}
                    className="space-y-3"
                    data-testid="security-step-up-form"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="step-up-password">Password</Label>
                      <PasswordInput
                        id="step-up-password"
                        value={stepUpPassword}
                        onChange={(e) => setStepUpPassword(e.target.value)}
                        placeholder="Enter your password"
                        autoFocus
                        autoComplete="current-password"
                        data-testid="security-step-up-password-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={!stepUpPassword || stepUpLoading}
                      className="w-full"
                      data-testid="security-step-up-submit-button"
                    >
                      {stepUpLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Confirming...
                        </>
                      ) : (
                        "Confirm with password"
                      )}
                    </Button>
                  </form>
                )}

                {passkeys.length > 0 && (
                  <>
                    {stepUpHasPassword && (
                      <div className="relative">
                        <Separator />
                        <span className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-1/2 bg-card px-2 text-xs uppercase tracking-wider text-muted-foreground">
                          or
                        </span>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant={stepUpHasPassword ? "outline" : "default"}
                      onClick={handleStepUpWithPasskey}
                      disabled={stepUpLoading}
                      className="w-full"
                      data-testid="security-step-up-passkey-button"
                    >
                      <Fingerprint className="mr-2 h-4 w-4" />
                      Confirm with passkey
                    </Button>
                  </>
                )}

                {!stepUpHasPassword && passkeys.length === 0 && (
                  <Alert variant="warning" data-testid="security-step-up-no-method-empty-state">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      To change security settings, please add a password or passkey first.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}

            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setStepUpPending(null);
                setStepUpPassword("");
                setError(null);
              }}
              className="w-full"
              disabled={stepUpLoading}
              data-testid="security-step-up-cancel-button"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
